import { useState } from 'react';
import { Download, Undo2, Check, X } from 'lucide-react';
import { adminApi } from '../../hooks/useAdminAi';

const API = process.env.REACT_APP_BACKEND_URL;
const card = 'bg-zinc-900 border border-zinc-800 rounded-xl p-4';
const fmt = (d) => new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

export function BetaReports({ reports }) {
  const [open, setOpen] = useState(null);
  return (
    <div className={card} data-testid="admin-ai-reports">
      <h3 className="font-bold text-white mb-2">Beta reports</h3>
      {reports.length === 0 && <p className="text-xs text-zinc-500">No reports yet. Run beta tests, or wait for the nightly 05:00 run.</p>}
      <div className="space-y-2 max-h-72 overflow-y-auto">
        {reports.map((r) => (
          <div key={r.report_id} className="border border-zinc-800 rounded-lg p-2" data-testid={`admin-ai-report-${r.report_id}`}>
            <div className="flex items-center gap-2 text-xs">
              <span className="font-bold" style={{ color: r.passed === r.total ? '#00D26A' : '#FFD700' }}>{r.passed}/{r.total} passed</span>
              <span className="text-zinc-500">{fmt(r.created_at)} · {r.triggered_by}</span>
              <button onClick={() => setOpen(open === r.report_id ? null : r.report_id)} className="ml-auto text-zinc-300 hover:text-white" data-testid={`admin-ai-report-toggle-${r.report_id}`}>{open === r.report_id ? 'Hide' : 'Read'}</button>
              <a href={`${API}/api/admin-ai/beta/reports/${r.report_id}/download`} className="text-[#00D26A]" title="Download for your builder" data-testid={`admin-ai-report-download-${r.report_id}`}><Download className="w-4 h-4" /></a>
            </div>
            {open === r.report_id && <pre className="mt-2 text-xs text-zinc-300 whitespace-pre-wrap font-sans">{r.report_markdown}</pre>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function NotesBox({ notes, run, busy }) {
  const [feature, setFeature] = useState('');
  const [note, setNote] = useState('');
  const submit = () => run('note', '/notes', { body: JSON.stringify({ feature, note }) }, () => { setFeature(''); setNote(''); return 'Note saved — it will be included in the next beta report.'; });
  const pending = notes.filter((n) => !n.used_in_report);
  return (
    <div className={card} data-testid="admin-ai-notes">
      <h3 className="font-bold text-white mb-1">Test notes</h3>
      <p className="text-xs text-zinc-500 mb-2">{pending.length} notes waiting for the next report (yours + beta testers via Flip Buddy).</p>
      <input value={feature} onChange={(e) => setFeature(e.target.value)} placeholder="Feature (e.g. Batch Checker)" className="w-full mb-2 bg-black border border-zinc-800 rounded px-3 py-2 text-sm" data-testid="admin-ai-note-feature" />
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="What happened vs what you expected" className="w-full bg-black border border-zinc-800 rounded px-3 py-2 text-sm" data-testid="admin-ai-note-text" />
      <button onClick={submit} disabled={!feature || note.length < 3 || busy === 'note'} className="mt-2 px-4 py-2 rounded-full text-sm font-bold disabled:opacity-50" style={{ background: '#00D26A', color: '#050505' }} data-testid="admin-ai-note-submit">Save note</button>
      <div className="mt-3 space-y-1 max-h-32 overflow-y-auto">
        {pending.slice(0, 10).map((n) => <div key={n.note_id} className="text-xs text-zinc-400"><span className="text-zinc-200">{n.feature}</span> — {n.note} <span className="text-zinc-600">({n.user_email})</span></div>)}
      </div>
    </div>
  );
}

export function FaqReview({ faqs, reload, setMsg }) {
  const act = async (id, body, label) => {
    try { await adminApi(`/faqs/${id}`, { method: 'PATCH', body: JSON.stringify(body) }); setMsg(label); reload(); } catch (e) { setMsg(e.message); }
  };
  return (
    <div className={card} data-testid="admin-ai-faqs">
      <h3 className="font-bold text-white mb-2">FAQ drafts</h3>
      {faqs.length === 0 && <p className="text-xs text-zinc-500">No FAQs yet. Once users have asked Flip Buddy some questions, click “Draft FAQs”.</p>}
      <div className="space-y-2">
        {faqs.map((f) => <FaqRow key={f.faq_id} f={f} act={act} />)}
      </div>
    </div>
  );
}

function FaqRow({ f, act }) {
  const [q, setQ] = useState(f.question);
  const [a, setA] = useState(f.answer);
  const live = f.status === 'published';
  return (
    <div className="border border-zinc-800 rounded-lg p-3" data-testid={`admin-ai-faq-${f.faq_id}`}>
      <div className="flex items-center gap-2 mb-2 text-xs">
        <span className="px-2 py-0.5 rounded-full font-bold" style={{ background: live ? '#00D26A22' : '#FFD70022', color: live ? '#00D26A' : '#FFD700' }}>{live ? 'Live' : 'Draft'}</span>
        <span className="text-zinc-500">asked ~{f.ask_count}×</span>
      </div>
      <input value={q} onChange={(e) => setQ(e.target.value)} className="w-full bg-black border border-zinc-800 rounded px-2 py-1 text-sm font-bold mb-1" data-testid={`admin-ai-faq-q-${f.faq_id}`} />
      <textarea value={a} onChange={(e) => setA(e.target.value)} rows={3} className="w-full bg-black border border-zinc-800 rounded px-2 py-1 text-xs" data-testid={`admin-ai-faq-a-${f.faq_id}`} />
      <div className="flex gap-2 mt-2">
        <button onClick={() => act(f.faq_id, { question: q, answer: a, status: 'published' }, 'FAQ published to the Help Centre.')} className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold" style={{ background: '#00D26A', color: '#050505' }} data-testid={`admin-ai-faq-publish-${f.faq_id}`}><Check className="w-3 h-3" />{live ? 'Save' : 'Approve & publish'}</button>
        <button onClick={() => act(f.faq_id, { status: live ? 'draft' : 'rejected' }, live ? 'FAQ unpublished.' : 'FAQ rejected.')} className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border border-zinc-700 text-zinc-300" data-testid={`admin-ai-faq-reject-${f.faq_id}`}><X className="w-3 h-3" />{live ? 'Unpublish' : 'Reject'}</button>
      </div>
    </div>
  );
}

export function ChangeLog({ changes, reload, setMsg }) {
  const undo = async (id) => {
    try { await adminApi(`/changes/${id}/undo`, { method: 'POST' }); setMsg('Change undone.'); reload(); } catch (e) { setMsg(e.message); }
  };
  return (
    <div className={card} data-testid="admin-ai-changes">
      <h3 className="font-bold text-white mb-2">Changes made by the Admin AI</h3>
      {changes.length === 0 && <p className="text-xs text-zinc-500">No changes yet.</p>}
      <div className="max-h-80 overflow-y-auto divide-y divide-zinc-800">
        {changes.map((c) => (
          <div key={c.change_id} className="flex items-center gap-3 py-2 text-xs" data-testid={`admin-ai-change-${c.change_id}`}>
            <span className="text-zinc-500 w-24 shrink-0">{fmt(c.created_at)}</span>
            <span className="font-bold text-zinc-200 w-28 shrink-0">{c.action.replace('_', ' ')}</span>
            <span className="text-zinc-400 flex-1 truncate">{c.barcode ? `${c.barcode}: ` : ''}{describe(c)} — {c.reason}</span>
            {c.undone ? <span className="text-zinc-600">undone</span> : (
              <button onClick={() => undo(c.change_id)} className="flex items-center gap-1 text-[#FFD700] hover:text-white" data-testid={`admin-ai-undo-${c.change_id}`}><Undo2 className="w-3 h-3" />Undo</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function describe(c) {
  if (c.action === 'flag_priority') return `${c.new.count} items set priority=${String(c.new.value)}`;
  if (c.action === 'fix_title') return `“${c.old || '(none)'}” → “${c.new}”`;
  return `${c.field} → £${c.new?.value ?? ''}`;
}
